import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaTrash, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { api } from '../../services/api';

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        setLoading(true);
        // Use the api service to get ticket details
        const response = await api.getClientTickets();
        const foundTicket = response.find(t => t.id === id);
        
        if (!foundTicket) {
          setError('Ticket non trouvé');
          toast.error('Impossible de charger les détails du ticket');
          return;
        }
        
        // Format the ticket data to ensure all fields are present
        const formattedTicket = {
          ...foundTicket,
          created_at: foundTicket.created_at || foundTicket.createdAt,
          updated_at: foundTicket.updated_at || foundTicket.updatedAt,
          // Include all fields from the database
          id: foundTicket.id,
          client_id: foundTicket.client_id,
          type: foundTicket.type,
          subject: foundTicket.subject,
          description: foundTicket.description,
          status: foundTicket.status,
          resolved_at: foundTicket.resolved_at,
          assigned_to: foundTicket.assigned_to,
          reservation_id: foundTicket.reservation_id,
          quote_id: foundTicket.quote_id
        };
        
        setTicket(formattedTicket);
      } catch (err) {
        console.error('Erreur lors du chargement du ticket:', err);
        setError('Erreur lors du chargement du ticket');
        toast.error('Erreur lors du chargement du ticket');
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [id]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'nouveau': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'en_cours': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'resolu': return 'bg-green-100 text-green-800 border-green-200';
      case 'ferme': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      nouveau: 'Nouveau',
      en_cours: 'En cours',
      resolu: 'Résolu',
      ferme: 'Fermé'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateString).toLocaleDateString('fr-FR', options);
    } catch (error) {
      console.error('Erreur de format de date:', error);
      return 'Date invalide';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin h-8 w-8 text-blue-500" />
        <span className="ml-2">Chargement du ticket...</span>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="text-red-600 mb-4">{error || 'Ticket non trouvé'}</div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <FaArrowLeft className="mr-2" /> Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <FaArrowLeft className="mr-2" /> Retour à la liste des tickets
        </button>
        
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Détails du ticket</h1>
          <div className="flex space-x-2">
            <button
              onClick={() => navigate(`/client/tickets/edit/${ticket.id}`)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaEdit className="mr-2 h-4 w-4" />
              Modifier
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        {/* En-tête avec sujet et statut */}
        <div className="px-6 py-5 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {ticket.subject}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                <span className="font-medium">ID:</span> {ticket.id} • 
                <span className="font-medium ml-1">Type:</span> {ticket.type === 'reclamation' ? 'Réclamation' : 'Demande de remboursement'}
              </p>
            </div>
            <span className={`mt-2 sm:mt-0 px-3 py-1 text-sm font-semibold rounded-full border ${getStatusBadgeClass(ticket.status)}`}>
              {getStatusLabel(ticket.status)}
            </span>
          </div>
        </div>

        {/* Informations principales */}
        <div className="px-6 py-5">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Détails du ticket</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">ID du ticket</h4>
                <p className="mt-1 text-sm text-gray-900 font-mono">{ticket.id}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Date de création</h4>
                <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.created_at)}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Dernière mise à jour</h4>
                <p className="mt-1 text-sm text-gray-900">{ticket.updated_at ? formatDate(ticket.updated_at) : 'Jamais mis à jour'}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Statut</h4>
                <p className="mt-1 text-sm text-gray-900 capitalize">{getStatusLabel(ticket.status)}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Type de ticket</h4>
                <p className="mt-1 text-sm text-gray-900 capitalize">
                  {ticket.type === 'reclamation' ? 'Réclamation' : 'Demande de remboursement'}
                </p>
              </div>
              
              {ticket.quote_id && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Devis lié</h4>
                  <button 
                    onClick={() => navigate(`/client/quotes/${ticket.quote_id}`)}
                    className="mt-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Voir le devis #{ticket.quote_id}
                  </button>
                </div>
              )}
              
              {ticket.reservation_id && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Réservation liée</h4>
                  <button 
                    onClick={() => navigate(`/client/quotes/${ticket.reservation_id}`)}
                    className="mt-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Voir la réservation #{ticket.reservation_id}
                  </button>
                </div>
              )}
              
              {ticket.resolved_at && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Date de résolution</h4>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(ticket.resolved_at)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description détaillée */}
        <div className="border-t border-gray-200 px-6 py-5">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Description détaillée</h4>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            {ticket.description ? (
              <div className="whitespace-pre-wrap text-gray-800 text-sm">
                {ticket.description}
              </div>
            ) : (
              <p className="text-gray-500 italic">Aucune description fournie</p>
            )}
          </div>
        </div>

        {/* Notes et informations supplémentaires */}
        {ticket.admin_notes && (
          <div className="border-t border-gray-200 px-6 py-5 bg-yellow-50">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Notes de l'administrateur</h4>
            <div className="prose prose-sm max-w-none text-gray-700 bg-white p-4 rounded-lg border border-yellow-100">
              {ticket.admin_notes}
            </div>
          </div>
        )}

        {/* Pièces jointes si disponibles */}
        {ticket.attachments && ticket.attachments.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-5">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Pièces jointes</h4>
            <div className="flex flex-wrap gap-3">
              {ticket.attachments.map((attachment, index) => (
                <a
                  key={index}
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {attachment.name || `Pièce jointe ${index + 1}`}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDetails;
